import { Card, CardContent } from '@recipes/ui';
import {
  Camera,
  Clapperboard,
  FileText,
  Link2,
  MapPin,
  PenLine,
  ScanBarcode,
  Search,
  Sparkles,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { AiConsentNotice } from '@/features/shared/ai-consent-notice';
import { useAiConsent, useAiUnlocked, useConfig, useFeature } from '@/features/shared/hooks';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';

interface Entry {
  to: string;
  icon: ComponentType<{ className?: string }>;
  key: string;
  ai?: boolean;
  /** Needs the server's video model (Gemini). */
  video?: boolean;
  tasting?: boolean;
  scan?: boolean;
  testId: string;
}

const ENTRIES: Entry[] = [
  { to: '/recipes/new', icon: PenLine, key: 'manual', testId: 'add-manual' },
  { to: '/add/import', icon: Link2, key: 'import', testId: 'add-import' },
  { to: '/add/text', icon: FileText, key: 'text', ai: true, testId: 'add-text' },
  { to: '/add/images', icon: Camera, key: 'images', ai: true, testId: 'add-images' },
  {
    to: '/add/video',
    icon: Clapperboard,
    key: 'video',
    ai: true,
    video: true,
    testId: 'add-video',
  },
  { to: '/add/ask', icon: Search, key: 'ask', ai: true, testId: 'add-ask' },
  { to: '/assistant', icon: Sparkles, key: 'assistant', ai: true, testId: 'add-assistant' },
  { to: '/tasting/new', icon: MapPin, key: 'tasting', tasting: true, testId: 'add-tasting' },
  { to: '/catalog/scan', icon: ScanBarcode, key: 'scan', scan: true, testId: 'add-scan' },
];

export function AddMenuPage() {
  const { t } = useTranslation();
  const aiEnabled = useFeature('ai');
  const consented = useAiConsent();
  const unlocked = useAiUnlocked();
  const tastingEnabled = useFeature('tasting_notes');
  const scanEnabled = useFeature('ingredient_catalog');
  const { data: config } = useConfig();
  const videoEnabled = config?.ai.video ?? false;
  const entries = ENTRIES.filter(
    (e) =>
      (!e.ai || aiEnabled) &&
      (!e.video || videoEnabled) &&
      (!e.tasting || tastingEnabled) &&
      (!e.scan || scanEnabled),
  );

  return (
    <PageContainer>
      <FeaturePageHeader title={t('add.title')} description={t('add.description')} />
      {aiEnabled && !consented && <AiConsentNotice />}
      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {entries.map((entry) => (
          <li key={entry.to}>
            <Card className="h-full transition-colors hover:bg-accent/40 focus-within:ring-2 focus-within:ring-ring">
              <Link
                to={entry.to}
                className="block h-full rounded-lg outline-none"
                data-testid={entry.testId}
              >
                <CardContent className="flex h-full gap-4 p-4 md:p-5">
                  <entry.icon className="mt-0.5 size-7 shrink-0 text-primary" aria-hidden />
                  <span>
                    <span className="block text-base font-semibold">
                      {t(`add.${entry.key}.title`)}
                    </span>
                    <span className="mt-1 block text-sm text-muted-foreground">
                      {t(`add.${entry.key}.description`)}
                    </span>
                    {entry.ai && !consented && (
                      <span className="mt-2 block text-xs text-muted-foreground">
                        {t(unlocked ? 'add.consent.short' : 'add.locked.short')}
                      </span>
                    )}
                  </span>
                </CardContent>
              </Link>
            </Card>
          </li>
        ))}
      </ul>
    </PageContainer>
  );
}
